import ms from "ms";
import pLimit from "p-limit";
import get from "lodash/get";
import { pEventIterator } from "p-event";
import dayjs from "dayjs";
import { vcardParser } from "@whatslly/commons/vcardUtils";
import type { ContactModel, Message, ModelType, StatusModel, XIdType } from "@whatslly/types/instant-messaging/WhatsappTypes";
import type { WhatsappSelectorsType } from "@whatslly/types/integrations/im/WhatsappGetSelectorsResponse";
import type { WhatsAppApi } from "./injectWhatsappApi";
import { injectWhatsappApi } from "./injectWhatsappApi";
import { getNamespace, injectWhatsappApiComet } from "./injectWhatsappApiComet";
import mimeTypes from "../nginx-types.json";
import { getPrettyPhone, retry } from "@whatslly/commons/utils";
import type {
  DeferredMedia,
  EmittedMessage,
  IEmitterSettings,
  SerializedMessage,
  SerializedMessageMedia,
} from "@whatslly/types/instant-messaging/messagesEmitter";
import { AllChats } from "@whatslly/types/instant-messaging/messagesEmitter";
import { isPhoneAllowed } from "@whatslly/instant-messaging/messageSyncUtils";
import { EventEmitter } from "node:events";
export { AllChats } from "@whatslly/types/instant-messaging/messagesEmitter";

const WAP_LID_SUFFIX = "@lid";
const DEFAULT_MIME_TYPE = "application/octet-stream";

type GetMe = { id: string; server: string; phone: string; isBusiness?: boolean; name?: string } | undefined;
type CallData = {
  timestamp: number;
  date: string;
  direction: "Outgoing" | "Incoming";
  from: string;
  to: string;
  outcome: string;
  callType: "Video" | "Audio";
  id: string;
  text: string;
};

type MediaData = {
  directPath: string;
  filehash: string;
  encFilehash: string;
  mediaKey: string;
  mediaKeyTimestamp: string;
  type: string;
};

export const DEFAULT_SCRAPER_SETTINGS = {
  syncNewsLetterMessages: false,
  phonesWhitelist: new Set<string>(),
  blockedPhones: [] as string[],
  maxMessageAge: ms("90 days"),
  mediaMessageTypes: new Set(["image", "photo", "video", "document", "ptt", "sticker", "ptv", "audio", "ciphertext"] as const),
  mediaMessageMimeTypes: undefined,
  textMessageTypes: new Set(["chat", "call_log", "revoked", "location"] as const),
  vcardMessageTypes: new Set(["vcard", "multi_vcard"] as const),
  pollMessageTypes: new Set(["poll_creation"] as const),
  eventMessageTypes: new Set(["event_creation"] as const),
  mediaMode: "base64",
  maxFileSizeBytes: 4_400_000,
  listenToNewMessages: false,
  ignoreOldMessages: false,
  scrollToEarliestMessage: true,
  abortSignal: new AbortController().signal,
  emitAckChanges: false,
  collectGroupParticipants: false,
  syncStatusMessages: false,
  messageIdsToSync: undefined,
  syncTimestampRange: undefined,
  scrollTimestamps: {},
} as const;

const newsletterIdPattern = "@newsletter";

declare const window: {
  TUVIS_LOG?: (obj: any, msg?: string) => void;
  importNamespace: (module: string) => any;
  importDefault: (module: string) => any;
  Debug?: {
    VERSION?: string;
  };
};

export class MessageScraper {
  private readonly settings: IEmitterSettings;
  private readonly selectors: WhatsappSelectorsType;
  private readonly whatsappAPI: WhatsAppApi;
  private readonly mediaLimit = pLimit(200);
  private readonly eventListeners: Record<
    "newMessage" | "editMessage" | "messageAck" | "newsLetterMessage" | "newsLetterEditMessage" | "newsLetterMessageAck",
    {
      on: string;
      off: string;
      bind: string;
      event: string;
      chatEvent?: boolean;
      oncePerMessage: boolean;
    }[]
  >;
  constructor(
    private readonly chatModel: ModelType | ModelType[] | typeof AllChats,
    settings: Partial<IEmitterSettings> = {},
    private readonly logger?: { warn: (data: any, message: string) => void; error: (data: any, message: string) => void }
  ) {
    if (!MessageScraper.selectors || !MessageScraper.whatsappAPI) {
      throw new Error("MessageScraper.inject must be called before usage");
    }
    this.selectors = MessageScraper.selectors;
    this.whatsappAPI = MessageScraper.whatsappAPI;
    this.settings = {
      syncNewsLetterMessages: settings.syncNewsLetterMessages ?? DEFAULT_SCRAPER_SETTINGS.syncNewsLetterMessages,
      phonesWhitelist: settings.phonesWhitelist ?? DEFAULT_SCRAPER_SETTINGS.phonesWhitelist,
      blockedPhones: settings.blockedPhones ?? DEFAULT_SCRAPER_SETTINGS.blockedPhones,
      maxMessageAge: settings.maxMessageAge ?? DEFAULT_SCRAPER_SETTINGS.maxMessageAge,
      mediaMessageTypes: settings.mediaMessageTypes ?? DEFAULT_SCRAPER_SETTINGS.mediaMessageTypes,
      textMessageTypes: settings.textMessageTypes ?? DEFAULT_SCRAPER_SETTINGS.textMessageTypes,
      vcardMessageTypes: settings.vcardMessageTypes ?? DEFAULT_SCRAPER_SETTINGS.vcardMessageTypes,
      pollMessageTypes: settings.pollMessageTypes ?? DEFAULT_SCRAPER_SETTINGS.pollMessageTypes,
      eventMessageTypes: settings.eventMessageTypes ?? DEFAULT_SCRAPER_SETTINGS.eventMessageTypes,
      mediaMode: settings.mediaMode ?? DEFAULT_SCRAPER_SETTINGS.mediaMode,
      maxFileSizeBytes: settings.maxFileSizeBytes ?? DEFAULT_SCRAPER_SETTINGS.maxFileSizeBytes,
      listenToNewMessages: settings.listenToNewMessages ?? DEFAULT_SCRAPER_SETTINGS.listenToNewMessages,
      scrollToEarliestMessage: settings.scrollToEarliestMessage ?? DEFAULT_SCRAPER_SETTINGS.scrollToEarliestMessage,
      abortSignal: settings.abortSignal ?? DEFAULT_SCRAPER_SETTINGS.abortSignal,
      mediaMessageMimeTypes: settings.mediaMessageMimeTypes ?? DEFAULT_SCRAPER_SETTINGS.mediaMessageMimeTypes,
      emitAckChanges: settings.emitAckChanges ?? DEFAULT_SCRAPER_SETTINGS.emitAckChanges,
      collectGroupParticipants: settings.collectGroupParticipants ?? DEFAULT_SCRAPER_SETTINGS.collectGroupParticipants,
      syncStatusMessages: settings.syncStatusMessages ?? DEFAULT_SCRAPER_SETTINGS.syncStatusMessages,
      messageIdsToSync: settings.messageIdsToSync ?? DEFAULT_SCRAPER_SETTINGS.messageIdsToSync,
      syncTimestampRange: settings.syncTimestampRange ?? DEFAULT_SCRAPER_SETTINGS.syncTimestampRange,
      scrollTimestamps: settings.scrollTimestamps ?? DEFAULT_SCRAPER_SETTINGS.scrollTimestamps,
      ignoreOldMessages: settings.ignoreOldMessages ?? DEFAULT_SCRAPER_SETTINGS.ignoreOldMessages,
    };
    this.settings.abortSignal.addEventListener("abort", () => this.mediaLimit.clearQueue(), { once: true });
    this.eventListeners = {
      newsLetterMessage: [
        {
          on: this.selectors.newsLetter.onFn,
          off: this.selectors.newsLetter.onFn,
          bind: this.selectors.newsLetter.onFnBind,
          event: this.selectors.newsLetter.newMessageEvent,
          oncePerMessage: true,
        },
      ],
      newsLetterEditMessage: [
        {
          on: this.selectors.newsLetter.onFn,
          off: this.selectors.newsLetter.onFn,
          bind: this.selectors.newsLetter.onFnBind,
          event: this.selectors.newsLetter.editMessageEvent,
          oncePerMessage: false,
        },
      ],
      newsLetterMessageAck: [
        {
          on: this.selectors.newsLetter.onFn,
          off: this.selectors.newsLetter.onFn,
          bind: this.selectors.newsLetter.onFnBind,
          event: this.selectors.newsLetter.messageAckEvent,
          oncePerMessage: false,
        },
      ],
      newMessage: [
        {
          on: this.selectors.msg.onFn,
          off: this.selectors.msg.onFn,
          bind: this.selectors.msg.onFnBind,
          event: this.selectors.msg.newMessageEvent,
          oncePerMessage: true,
        },
      ],
      editMessage: [
        {
          on: this.selectors.msg.onFn,
          off: this.selectors.msg.onFn,
          bind: this.selectors.msg.onFnBind,
          event: this.selectors.msg.editMessageEvent,
          oncePerMessage: false,
        },
      ],
      messageAck: [
        {
          on: this.selectors.msg.onFn,
          off: this.selectors.msg.onFn,
          bind: this.selectors.msg.onFnBind,
          event: this.selectors.msg.messageAckEvent,
          oncePerMessage: false,
        },
      ],
    };
    if (this.chatModel !== AllChats && !Array.isArray(this.chatModel)) {
      this.eventListeners.newMessage.push({
        on: this.selectors.chat.scrollNewMessagesOnFn,
        off: this.selectors.chat.scrollNewMessagesOffFn,
        bind: this.selectors.chat.scrollNewMessagesEventBind,
        event: this.selectors.chat.scrollNewMessagesEvent,
        chatEvent: true,
        oncePerMessage: true,
      });
    }
  }

  public static selectors: WhatsappSelectorsType | undefined;
  public static whatsappAPI: WhatsAppApi | undefined;
  static inject(selectors: WhatsappSelectorsType) {
    MessageScraper.selectors ??= selectors;
    MessageScraper.whatsappAPI = window.importNamespace === undefined ? injectWhatsappApi(selectors) : injectWhatsappApiComet(selectors);
  }

  private static activeChat: ModelType | undefined;
  private static handlers = new Map<string, Map<(...args: any[]) => void, () => void>>();
  static addEventListener(event: "activeChatChange", callback: any) {
    if (!MessageScraper.selectors || !MessageScraper.whatsappAPI) {
      throw new Error("MessageScraper.inject must be called before usage");
    }
    const { selectors, whatsappAPI } = MessageScraper;
    let handlers = MessageScraper.handlers.get(event);
    if (!handlers) {
      handlers = new Map();
      MessageScraper.handlers.set(event, handlers);
    }
    const handler = (chat: ModelType) => {
      if (selectors.chatActiveModal.find.every((key) => get(chat, key)) && chat !== MessageScraper.activeChat) {
        MessageScraper.activeChat = chat;
        callback(chat);
      }
    };
    const removeEventListener = () =>
      get(whatsappAPI, selectors.chat.offFn).call(get(whatsappAPI, selectors.chat.onFnBind), selectors.chat.changeChatEvent, handler);
    get(whatsappAPI, selectors.chat.onFn).call(get(whatsappAPI, selectors.chat.onFnBind), selectors.chat.changeChatEvent, handler);
    handlers.set(callback, removeEventListener);
    return removeEventListener;
  }
  addEventListener(event: keyof typeof MessageScraper.prototype.eventListeners, callback: (data: { message: Message; chat: ModelType }) => void) {
    let handlers = MessageScraper.handlers.get(event);
    if (!handlers) {
      handlers = new Map();
      MessageScraper.handlers.set(event, handlers);
    }

    const emitted = new Set<string>();
    const listeners = this.eventListeners[event];
    const handler = async (message: Message) => {
      const isSentByMe = get(message, this.selectors.msg.isSentByMe);
      let apiSelector: EventEmitter = this.whatsappAPI.Chat;
      let chatId = isSentByMe ? message.to : message.from;
      const isGroupMsg = MessageScraper.getIsGroup({}, message.from?._serialized ?? "", message.to?._serialized ?? "");
      if (message?.senderObj?.collection && message?.senderObj?.id && !isGroupMsg) {
        apiSelector = message.senderObj.collection;
        if (isSentByMe === false) {
          chatId = message.senderObj.id;
        }
      }

      if (MessageScraper.getIsStatus(message)) {
        apiSelector = this.whatsappAPI.Status;
        chatId = isSentByMe ? message.from : message.author;
      } else if (MessageScraper.getIsNewsletter(message)) {
        apiSelector = this.whatsappAPI.NewsLetter;
        chatId = message.id?.remote === undefined ? message.id : message.id.remote;
      }

      const findMethod = get(apiSelector, this.selectors.chat.findMethod);
      const id = MessageScraper.getMessageId(message);

      try {
        const chat = await findMethod.call(apiSelector, chatId);
        const thisChatModelId = get(this.chatModel, this.selectors.contact.id);
        const currentChatId = get(chat, this.selectors.contact.id);
        const isMatchingChat =
          this.chatModel === AllChats ||
          this.chatModel === chat ||
          (thisChatModelId && currentChatId && currentChatId === thisChatModelId) ||
          (Array.isArray(this.chatModel) && this.chatModel.some((model) => model === chat));
        if (!chat || !isMatchingChat) return;
        if (!emitted.has(id) || !listeners[0].oncePerMessage) {
          emitted.add(id);
          callback({ message, chat });
        }
      } catch (err) {
        this.logger?.error(
          { err, messageType: message?.type, chatId: chatId?.user, messageId: id, whatsAppVersion: window?.Debug?.VERSION },
          "Error emitting message on event listener handler"
        );
      }
    };
    const bulkHandler = (messages: Message[]) => messages.forEach(handler);

    const removeEventListener = () => {
      listeners.forEach(({ off, bind, event, chatEvent }) => {
        const obj = chatEvent ? this.chatModel : this.whatsappAPI;
        get(obj, off).call(get(obj, bind), event, chatEvent ? bulkHandler : handler);
      });
    };
    handlers.set(callback, removeEventListener);
    listeners.forEach(({ on, bind, event, chatEvent }) => {
      const obj = chatEvent ? this.chatModel : this.whatsappAPI;
      get(obj, on).call(get(obj, bind), event, chatEvent ? bulkHandler : handler);
    });
    return removeEventListener;
  }
  static removeEventListener(event: string, callback: any) {
    const handlers = MessageScraper.handlers.get(event);
    handlers?.get(callback)?.();
    handlers?.delete(callback);
  }
  removeEventListener(event: string, callback: any) {
    MessageScraper.removeEventListener(event, callback);
  }

  static getActiveChat() {
    if (!MessageScraper.selectors) {
      throw new Error("MessageScraper.inject must be called before usage");
    }
    const activeChat = get(MessageScraper.whatsappAPI, MessageScraper.selectors.chatActiveModal.fn).call(
      get(MessageScraper.whatsappAPI, MessageScraper.selectors.chatActiveModal.bind)
    );
    if (activeChat) return activeChat;
    const activeNewsLetter = get(MessageScraper.whatsappAPI, MessageScraper.selectors.chatActiveModalNewsLetter.fn).call(
      get(MessageScraper.whatsappAPI, MessageScraper.selectors.chatActiveModalNewsLetter.bind)
    );
    if (activeNewsLetter) return activeNewsLetter;
    return undefined;
  }

  static getIsGroup(chat: any, senderId: string, recipientId: string) {
    let isGroup;

    if (MessageScraper.selectors && chat && Object.keys(chat).length) {
      const selectors = MessageScraper.selectors;
      if (typeof window.importNamespace === "undefined") {
        isGroup = get(chat, selectors.selectedContact.isGroup) ?? false;
      } else {
        isGroup = getNamespace(selectors.nameSpaceChatGetter?.name ?? ["WAWebChatGetters"]).getIsGroup(chat);
      }
    }

    if (!isGroup && (senderId?.endsWith("@g.us") || recipientId?.endsWith("@g.us"))) {
      isGroup = true;
    }

    return isGroup;
  }

  static getIsStatus(message: Message) {
    return message.id?._serialized?.includes("status@broadcast");
  }

  static getIsNewsletter(message: Message) {
    return message.id?._serialized?.includes(newsletterIdPattern);
  }

  static getIsBroadcast(message: Message) {
    const idMatch = message.id?._serialized?.includes("@broadcast");
    const hasBroadcastId = get(message, MessageScraper.selectors?.msg?.broadcastId ?? "broadcastId") !== undefined;
    return (idMatch || hasBroadcastId) && !MessageScraper.getIsStatus(message);
  }

  static getIsLid(chatId: XIdType) {
    return Boolean(chatId?._serialized?.includes("@lid"));
  }

  static getMe() {
    if (typeof window.importNamespace === "undefined") {
      return MessageScraper.getMeWebpack();
    }
    return MessageScraper.getMeComet();
  }

  static getMessageId(message: Message): string {
    if (!MessageScraper.selectors) {
      throw new Error("MessageScraper.inject must be called before usage");
    }

    return get(message, MessageScraper.selectors.msg.serializedMessageId);
  }

  static getContactPhone(contact?: ContactModel): string {
    if (!MessageScraper.selectors) {
      throw new Error("MessageScraper.inject must be called before usage");
    }
    const contactId = MessageScraper.getModelIdFromModel(contact as any);
    return contactId?.split("@")[0] ?? get(contact, MessageScraper.selectors.contact.phone);
  }

  static relativeDateStr(unixTime: number) {
    if (!MessageScraper.whatsappAPI) {
      throw new Error("MessageScraper.inject must be called before usage");
    }
    return this.whatsappAPI?.Clock.relativeDateStr(unixTime);
  }

  static isMessageWithinSyncingTimeframe(
    message: EmittedMessage,
    { chatIdLatest, potentiallyUpdated }: { chatIdLatest: Record<string, number>; potentiallyUpdated: Record<string, number> }
  ) {
    return Boolean(
      message.isDeleted ||
        message.timestamp > (chatIdLatest?.[message.chatId] ?? 0) ||
        (potentiallyUpdated?.[message.serializedMessageId] && message.latestUpdate > potentiallyUpdated[message.serializedMessageId])
    );
  }

  private serializeArrayBuffer(arrayBuffer: ArrayBuffer, mimeType: string | undefined) {
    return new Promise<string>((resolve, reject) => {
      const blob = new Blob([arrayBuffer], { type: mimeType });
      const fileReader = new FileReader();
      fileReader.onload = () => resolve(fileReader.result as string);
      fileReader.onerror = (e) => reject(e);
      if (this.settings.mediaMode === "base64") {
        fileReader.readAsDataURL(blob);
      } else {
        fileReader.readAsBinaryString(blob);
      }
    });
  }

  private static isMsgFileResolved(message: Message) {
    if (!MessageScraper.selectors) {
      throw new Error("MessageScraper.inject must be called before usage");
    }
    const mediaUploadStage = get(message, MessageScraper.selectors.msg.mediaUploadStage ?? "mediaObject.uploadStage");
    const mediaDownloadStage = get(message, MessageScraper.selectors.msg.mediaDownloadStage ?? "mediaObject.downloadStage");
    const mediaStage = get(message, MessageScraper.selectors.msg.mediaStage ?? "mediaData.mediaStage");
    const isSentByMe = get(message, MessageScraper.selectors.msg.isSentByMe);
    const mediaSentByMeStageExpectedStatus = MessageScraper.selectors.msg.mediaSentByMeStageExpectedStatus ?? ["RESOLVED"];
    const mediaSentByYouStageExpectedStatus = MessageScraper.selectors.msg.mediaSentByYouStageExpectedStatus ?? ["RESOLVED"];
    const mediaUploadStageExpectedStatus = MessageScraper.selectors.msg.mediaUploadStageExpectedStatus ?? ["UPLOADED"];
    const mediaDownloadStageExpectedStatus = MessageScraper.selectors.msg.mediaDownloadStageExpectedStatus ?? ["RESOLVED"];
    const isValidMediaStage = (isSentByMe ? mediaSentByMeStageExpectedStatus : mediaSentByYouStageExpectedStatus).includes(mediaStage);
    const isValidDownloadUploadStage = isSentByMe
      ? mediaUploadStageExpectedStatus.includes(mediaUploadStage)
      : mediaDownloadStageExpectedStatus.includes(mediaDownloadStage);
    return isValidMediaStage || isValidDownloadUploadStage;
  }

  private static isMsgFileErrored(message: Message) {
    if (!MessageScraper.selectors) {
      throw new Error("MessageScraper.inject must be called before usage");
    }
    const mediaUploadStage = get(message, MessageScraper.selectors.msg.mediaUploadStage ?? "mediaObject.uploadStage");
    const mediaDownloadStage = get(message, MessageScraper.selectors.msg.mediaDownloadStage ?? "mediaObject.downloadStage");
    const mediaStage = get(message, MessageScraper.selectors.msg.mediaStage ?? "mediaData.mediaStage");
    const invalidMediaStages = MessageScraper.selectors.msg.invalidMediaStages ?? ["ERROR", "FETCHING"];
    return invalidMediaStages.some(
      (invalid) => mediaStage?.includes(invalid) || mediaUploadStage?.includes(invalid) || mediaDownloadStage?.includes(invalid)
    );
  }

  private getMediaMessageMetadata(message: Message): SerializedMessageMedia {
    const size = get(message, this.selectors.msg.mediaSize);
    const mediaFilename = get(message, this.selectors.msg.mediaFilename);
    const mimeType = get(message, this.selectors.msg.mimetype)?.split(";")[0] ?? DEFAULT_MIME_TYPE;
    const fileName =
      mediaFilename ||
      (() => {
        const msgId = get(message, this.selectors.msg.id);
        if (mimeType && DEFAULT_MIME_TYPE !== mimeType && mimeType in mimeTypes) {
          const mimeTypeFromDb = mimeTypes[mimeType as keyof typeof mimeTypes];
          if (mimeTypeFromDb && Array.isArray(mimeTypeFromDb.extensions) && mimeTypeFromDb.extensions.length > 0) {
            return `${msgId}.${mimeTypeFromDb.extensions[0]}`;
          }
        }
        return `${msgId}_tuvis`;
      })();

    return {
      fileName,
      mimeType,
      size,
    };
  }

  private async downloadMessageMedia(message: Message, type: string, retries = 5): Promise<SerializedMessageMedia | undefined> {
    try {
      const media = this.getMediaMessageMetadata(message);
      if (media.size && media.size > this.settings.maxFileSizeBytes) {
        media.invalid = "size";
        return media;
      }

      if (!MessageScraper.isMsgFileResolved(message)) {
        const downloadMediaFn = get(message, this.selectors.msg.downloadMediaFn ?? "downloadMedia");
        await downloadMediaFn?.call(message, this.selectors.msg.downloadMediaParam ?? { downloadEvenIfExpensive: true, rmrReason: 1 });
      }
      if (MessageScraper.isMsgFileErrored(message)) {
        const messageId = MessageScraper.getMessageId(message);
        this.logger?.warn({ messageId, whatsAppVersion: window?.Debug?.VERSION }, "Media download errored");
        return;
      }
      const mockQpl = {
        addAnnotations: function () {
          return this;
        },
        addPoint: function () {
          return this;
        },
      };
      const downloadAbortController = new AbortController();
      const decrypted = await get(
        this.whatsappAPI.Downloads,
        this.selectors.downloadAndDecrypt.fn ?? "downloadAndMaybeDecrypt"
      )({
        directPath: get(message, this.selectors.msg.directPath ?? "directPath"),
        filehash: get(message, this.selectors.msg.filehash ?? "filehash"),
        encFilehash: get(message, this.selectors.msg.encFilehash ?? "encFilehash"),
        mediaKey: get(message, this.selectors.msg.mediaKey ?? "mediaKey"),
        mediaKeyTimestamp: get(message, this.selectors.msg.mediaKeyTimestamp ?? "mediaKeyTimestamp"),
        type,
        signal: downloadAbortController.signal,
        downloadQpl: mockQpl,
      });

      if (!media.size && media.size >= this.settings.maxFileSizeBytes) {
        media.invalid = "size";
        return media;
      }

      if (this.settings.mediaMessageMimeTypes?.size && !this.settings.mediaMessageMimeTypes.has(media.mimeType)) {
        media.invalid = "mimeType";
        return media;
      }

      media[this.settings.mediaMode === "base64" ? "dataURI" : "binary"] = await this.serializeArrayBuffer(decrypted, media.mimeType);
      return media;
    } catch (err) {
      const messageId = MessageScraper.getMessageId(message);
      this.logger?.error({ err, messageId, retries, whatsAppVersion: window?.Debug?.VERSION }, "Error downloading media");
      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return this.downloadMessageMedia(message, type, retries - 1);
      }
      return;
    }
  }

  private getVcardNames(message: Message) {
    const type = this.coercedMessageType(message);
    const vcards: string[] = [];
    if (type === "vcard") {
      const vcard = get(message, this.selectors.msg.msgBody);
      if (vcard) vcards.push(vcard);
    }
    if (type === "multi_vcard") {
      const vcardList = get(message, this.selectors.msg.vcardList);
      (Array.isArray(vcardList) ? vcardList : []).forEach((card) => {
        const vcard = get(card, this.selectors.msg.vcard);
        if (vcard) vcards.push(vcard);
      });
    }
    if (vcards.length) {
      const names = vcards
        .flatMap((data) => {
          const parsed = vcardParser(data);
          return [parsed?.fn?.[0]?.value ?? "", parsed?.tel?.[0]?.value ?? ""];
        })
        .filter(Boolean)
        .join("\n");
      if (!names) {
        return;
      }
      return { names, count: vcards.length };
    }
  }

  private async scrollToEarliest(maxIterations = 30) {
    if (!this.settings.scrollToEarliestMessage) {
      return;
    }
    const chats: ModelType[] =
      this.chatModel === AllChats
        ? (get(this.whatsappAPI, this.selectors.chat.models) ?? [])
        : Array.isArray(this.chatModel)
          ? this.chatModel
          : [this.chatModel];
    for (const chat of chats) {
      const chatId = this.getModelSerializedId(chat);
      const perChatTimestamp = this.settings.scrollTimestamps?.[chatId];
      const globalOldest = this.settings.syncTimestampRange?.oldest;
      const scrollTarget = perChatTimestamp ?? globalOldest ?? Date.now() - this.settings.maxMessageAge;
      const maxAge = scrollTarget / 1000;

      const getEarliestMessage = () => {
        const sorted = this.getChatMessages(chat)
          .slice(0)
          .sort((a, b) => (get(a, this.selectors.msg.t) ?? 0) - (get(b, this.selectors.msg.t) ?? 0));
        return get(sorted[0], this.selectors.msg.t) ?? 0;
      };

      while (getEarliestMessage() >= maxAge && maxIterations-- > 0 && !this.settings.abortSignal.aborted) {
        let loadEarlierMsgs;
        try {
          loadEarlierMsgs = await get(this.whatsappAPI.ConversationMsgs, this.selectors.ConversationMsgs.fn ?? "loadEarlierMsgs").call(
            this.whatsappAPI.ConversationMsgs,
            { chat }
          );
        } catch {
          // support also old loadEarlierMsgs function signature
          loadEarlierMsgs = await get(this.whatsappAPI.ConversationMsgs, this.selectors.ConversationMsgs.fn ?? "loadEarlierMsgs").call(
            this.whatsappAPI.ConversationMsgs,
            chat
          );
        }
        if (!loadEarlierMsgs?.length) {
          break;
        }
      }
    }
  }

  static getMeWebpack(): GetMe {
    if (!MessageScraper.selectors) {
      throw new Error("MessageScraper.inject must be called before usage");
    }
    const selectors = MessageScraper.selectors;
    const models = (get(this.whatsappAPI, selectors.contact.models) || []) as ContactModel[];
    const me = models.find((e) => selectors.me.find.every((meModel) => get(e, meModel)));
    const wid = (() => {
      const serializedId = get(me, selectors.me.serializedId);
      if (typeof serializedId === "string" && serializedId.includes(selectors.me.fallbackIndication)) {
        const fallbackSerializedId = get(me, selectors.me.fallbackSerializedId);
        if (fallbackSerializedId) {
          return fallbackSerializedId;
        }
      }
      if (serializedId) {
        return serializedId;
      }
      const statusIndex = get(this.whatsappAPI, selectors.me.statusIndex) as StatusModel["_index"];
      if (!statusIndex) {
        return "";
      }
      const meFromStatus = Object.values(statusIndex).find((sObj) => selectors.me.meFromStatus.every((att) => get(sObj, att)));
      if (!meFromStatus) {
        return "";
      }
      return get(meFromStatus, selectors.me.serializedId);
    })();

    if (!wid) {
      return;
    }

    const meModel = models.find((model) => get(model, selectors.me.serializedId) === wid);
    const nameAtt = meModel ? selectors.me.displayNameArray.find((item) => get(meModel, item)) : meModel;
    const name = nameAtt && meModel ? get(meModel, nameAtt) : undefined;
    const isBusiness = get(meModel, selectors.me.isBusiness) ?? false;

    const [phone, server] = wid.split("@");
    return { phone, server, name, isBusiness, id: wid };
  }

  static getMeComet(): GetMe {
    if (!MessageScraper.selectors) {
      throw new Error("MessageScraper.inject must be called before usage");
    }
    const selectors = MessageScraper.selectors;

    const WAWebUserPrefsMeUser = getNamespace(selectors.ng?.WAWebUserPrefsMeUser?.find ?? ["WAWebUserPrefsMeUser"]);
    if (!WAWebUserPrefsMeUser) {
      return;
    }
    const getMeUserFnName = (selectors.ng?.WAWebUserPrefsMeUser?.getMeUserFn ?? ["getMaybeMeUser", "getMeUser"]).find((fn) =>
      get(WAWebUserPrefsMeUser, fn)
    );
    if (!getMeUserFnName) {
      return;
    }
    const getMeUser = get(WAWebUserPrefsMeUser, getMeUserFnName).bind(WAWebUserPrefsMeUser);
    if (typeof getMeUser !== "function") {
      return;
    }
    const me = getMeUser();
    const serializedId = get(me, selectors.ng?.WAWebUserPrefsMeUser?.serialized ?? "_serialized");
    const models = this.whatsappAPI?.Contact.getModelsArray() ?? [];
    const meModel = models.find((model) => get(model, selectors.me.serializedId) === serializedId);
    const isBusiness = get(meModel, selectors.me.isBusiness) ?? false;
    const nameAtt = meModel ? selectors.me.displayNameArray.find((item) => get(meModel, item)) : meModel;
    const [phone, server] = serializedId.split("@");
    const name = nameAtt && meModel ? get(meModel, nameAtt) : undefined;
    const pushNameFromNamespace = getNamespace(["WAWebConnModel"]);
    return { phone, server, name: name ?? pushNameFromNamespace?.Conn?.pushname ?? "", isBusiness, id: serializedId };
  }

  #me: GetMe;
  get me() {
    this.#me ??= MessageScraper.getMe();
    return this.#me;
  }

  private async getLatestMessageUpdate(message: Message) {
    const timestamp = get(message, this.selectors.msg.t) ? get(message, this.selectors.msg.t) * 1000 : Date.now();
    let editMessageTimestamp: number = 0;
    try {
      editMessageTimestamp = get(message, this.selectors.msg.editTimestamp) || 0;
      if (!this.settings.emitAckChanges) {
        return Math.max(timestamp, editMessageTimestamp);
      }

      const ackInfo = await this.whatsappAPI.MessageInfo.sendQueryMsgInfo(message);
      const ackDates = this.selectors.ack.msgInfoDateKeys.flatMap((key) =>
        ackInfo[key].map((item: any) => get(item, this.selectors.ack.msgInfoDate) * 1000 || 0)
      );
      return Math.max(timestamp, editMessageTimestamp, ...ackDates);
    } catch (err) {
      if (editMessageTimestamp) {
        return Math.max(timestamp, editMessageTimestamp);
      }
      return timestamp;
    }
  }

  static displayNameAttr: string | undefined;
  private static getDisplayName(model: ContactModel | undefined) {
    if (!MessageScraper.selectors) {
      throw new Error("MessageScraper.inject must be called before usage");
    }
    const displayNameAttr = MessageScraper.selectors.contact.displayNameArray.find((item) => get(model, item));
    if (!displayNameAttr) return getPrettyPhone(MessageScraper.getContactPhone(model));
    return get(model, displayNameAttr);
  }

  private static getShortQuotedMessageId(message: Message) {
    if (!MessageScraper.selectors) {
      throw new Error("MessageScraper.inject must be called before usage");
    }
    const quotedMsgId = get(message, MessageScraper.selectors.msg.quotedMsgId);
    if (!quotedMsgId) {
      return;
    }
    return quotedMsgId.substring(quotedMsgId.length - 6, quotedMsgId.length);
  }

  private isDisappearingMessage(message: Message) {
    if (get(message, this.selectors.msg.isDisappearing) || get(message, this.selectors.msg.subtype) === "view_once_unavailable_fanout") {
      return true;
    }
    return false;
  }

  private async getMediaHost(mediaData: MediaData, signal: AbortSignal) {
    const getHostsInfo = get(this.whatsappAPI.Media.mediaHosts, this.selectors.getHostsInfo.fn).bind(this.whatsappAPI.Media.mediaHosts);
    const hostsInfo = await getHostsInfo({
      operation: get(this.whatsappAPI.Media.operations, this.selectors.mmsOperationsConst.download),
      directPath: mediaData.directPath,
      encFilehash: mediaData.encFilehash,
      type: mediaData.type,
      signal,
      forceRefresh: false,
    });
    const host = this.whatsappAPI.Media.selectHost({
      selectedHost: get(hostsInfo, this.selectors.getHostsInfo.selectedHost),
      fallbackHost: get(hostsInfo, this.selectors.getHostsInfo.fallbackHost),
      attemptCount: 0,
      lastHostUsed: null,
      lastFetchMadeProgress: !1,
    });
    return get(host, this.selectors.selectMediaHost.hostname);
  }

  private async isSignedMediaUrlResolved(signedUrl: string): Promise<boolean> {
    try {
      const response = await fetch(signedUrl, {
        method: "HEAD",
        signal: AbortSignal.timeout(10000),
      });
      return response.status === 200;
    } catch (error) {
      return false;
    }
  }

  private async loadSignedMediaUrl(mediaData: MediaData, withDirectPath = true) {
    const abortController = new AbortController();
    const host = await this.getMediaHost(mediaData, abortController.signal);

    return await this.whatsappAPI.Media.getSignedUrl({
      directPath: withDirectPath ? mediaData.directPath : null,
      encFilehash: mediaData.encFilehash,
      hostname: host,
      type: mediaData.type,
      mode: "auto",
    });
  }

  private getMediaData(message: Message, type: string): MediaData {
    return {
      directPath: get(message, this.selectors.msg.directPath ?? "directPath"),
      filehash: get(message, this.selectors.msg.filehash ?? "filehash"),
      encFilehash: get(message, this.selectors.msg.encFilehash ?? "encFilehash"),
      mediaKey: get(message, this.selectors.msg.mediaKey ?? "mediaKey"),
      mediaKeyTimestamp: get(message, this.selectors.msg.mediaKeyTimestamp ?? "mediaKeyTimestamp"),
      type,
    };
  }

  private async getSignedMedia(
    message: Message,
    type: any,
    signal?: AbortSignal
  ): Promise<{ deferredMedia: DeferredMedia; media: SerializedMessageMedia } | undefined> {
    try {
      const mediaData = await retry(
        () => {
          const result = this.getMediaData(message, type);
          if (!result.mediaKey || !result.encFilehash) {
            throw new Error("failed to load encFilehash or mediaKey");
          }
          return result;
        },
        { maxRetry: signal ? Infinity : 30, delayBetweenRetryMs: ms("1sec"), signal }
      );
      if (!mediaData.mediaKey || !mediaData.encFilehash) {
        return;
      }
      const metadata = this.getMediaMessageMetadata(message);
      if (metadata.size && metadata.size > this.settings.maxFileSizeBytes) {
        metadata.invalid = "size";
      }
      if (!this.settings.mediaMessageTypes.has(type)) {
        metadata.invalid = "messageType";
      }
      const isInvalid = Boolean(metadata.invalid);
      let signedUrl = await this.loadSignedMediaUrl(mediaData);
      const shouldDownloadFromDevice = isInvalid ? false : (await this.isSignedMediaUrlResolved(signedUrl)) === false;
      const isWithinDownloadSizeLimit = !metadata.size || metadata.size < 100 * 1024 * 1024; // Limit to 100MB so we don't download huge files that later turn to blobs, using lots of memory
      if (shouldDownloadFromDevice && isWithinDownloadSizeLimit) {
        const start = Date.now();
        try {
          this.logger?.warn(
            { messageId: MessageScraper.getMessageId(message), whatsAppVersion: window?.Debug?.VERSION },
            "Signed media url not reachable, trying to download again"
          );
          const downloadMediaFn = get(message, this.selectors.msg.downloadMediaFn ?? "downloadMedia");
          await downloadMediaFn?.call(message, this.selectors.msg.downloadMediaParam ?? { downloadEvenIfExpensive: true, rmrReason: 1 });
        } catch (error) {
          this.logger?.error(
            { err: error, messageId: MessageScraper.getMessageId(message), whatsAppVersion: window?.Debug?.VERSION },
            "Error downloading media"
          );
        } finally {
          signedUrl = await this.loadSignedMediaUrl(mediaData, false).catch(() => this.loadSignedMediaUrl(mediaData));
          const end = Date.now();
          this.logger?.warn(
            { messageId: MessageScraper.getMessageId(message), duration: end - start, whatsAppVersion: window?.Debug?.VERSION },
            "New signed media url"
          );
        }
      }
      return {
        deferredMedia: { url: signedUrl, key: mediaData.mediaKey },
        media: metadata,
      };
    } catch (err) {
      this.logger?.error(
        { err, messageId: MessageScraper.getMessageId(message), whatsAppVersion: window?.Debug?.VERSION },
        "Error loading signed media"
      );
      return;
    }
  }

  private static getModelIdFromModel(model: ModelType | ModelType["contact"] | undefined) {
    const selectors = MessageScraper.selectors;
    if (!selectors || !model) {
      return;
    }
    const idFnBind = get(model, selectors.modelMap.idFnBind);
    const idFn = get(model, selectors.modelMap.idFn);
    const id: string | undefined = idFn?.bind(idFnBind)();

    if (id?.endsWith(WAP_LID_SUFFIX)) {
      try {
        const WAWebApiContact = getNamespace(selectors.ng?.WAWebApiContact?.find ?? ["WAWebApiContact"]);
        const getPhoneNumber = get(WAWebApiContact, selectors.ng?.WAWebApiContact?.getPhoneNumber ?? "getPhoneNumber");
        const lid = getPhoneNumber?.bind(WAWebApiContact)(idFnBind);
        const lidFn = get(lid, selectors.ng?.WAWebApiContact?.lidFnBind ?? "toString");
        return lidFn?.bind(lid)() as string | undefined;
      } catch {
        return id;
      }
    }
    return id;
  }

  private getModelId(idFnBind: any | undefined) {
    if (!this.selectors || !idFnBind) {
      return;
    }
    const id: string | undefined = idFnBind?.toString();

    if (id?.endsWith(WAP_LID_SUFFIX)) {
      try {
        const WAWebApiContact = getNamespace(this.selectors.ng?.WAWebApiContact?.find ?? ["WAWebApiContact"]);
        const getPhoneNumber = get(WAWebApiContact, this.selectors.ng?.WAWebApiContact?.getPhoneNumber ?? "getPhoneNumber");
        const lid = getPhoneNumber?.bind(WAWebApiContact)(idFnBind);
        const lidFn = get(lid, this.selectors.ng?.WAWebApiContact?.lidFnBind ?? "toString");
        return lidFn?.bind(lid)() as string | undefined;
      } catch {
        return id;
      }
    }
    return id;
  }

  private getModelSerializedId(model: ModelType) {
    const id = get(model, this.selectors.chat.rawId);
    if (MessageScraper.getIsLid(id)) return this.whatsappAPI.Utils.lidToUser(id)?.user;
    return get(model, this.selectors.chat.id);
  }

  private extractPhoneFromId(phoneId: string | undefined) {
    const phoneValue = phoneId?.split("@")[0] ?? "";
    if (phoneId?.endsWith("@g.us")) {
      return phoneValue;
    }
    const nonNumericCharacter = phoneValue.search(/[^0-9]/);
    if (nonNumericCharacter !== -1) {
      return phoneValue.substring(0, nonNumericCharacter);
    }
    return phoneValue;
  }

  private extractPhone(message: Message, selector: string) {
    const phoneIdModel = get(message, selector);
    const phoneId = this.getModelId(phoneIdModel);
    return this.extractPhoneFromId(phoneId);
  }

  private async serializeMessage(message: Message, chat: ModelType) {
    const type = this.coercedMessageType(message);
    const raw_type = get(message, this.selectors.msg.type ?? "type");
    const isMediaMessage = DEFAULT_SCRAPER_SETTINGS.mediaMessageTypes.has(type);
    const isSentByMe = get(message, this.selectors.msg.isSentByMe);
    const contact = await this.whatsappAPI?.Contact?.get?.(get(chat, this.selectors.chat.rawId));
    const contactId = MessageScraper.getModelIdFromModel(contact);
    const contactName = MessageScraper.getDisplayName(contact);
    const authorPhone = this.extractPhone(message, this.selectors.msg.authorPhoneId ?? "id.participant");
    const senderPhone = this.extractPhone(message, this.selectors.msg.senderPhoneId ?? "__x_from");
    const receiverPhone = this.extractPhone(message, this.selectors.msg.recipientPhoneId ?? "__x_to");
    // Get notifyName first for group message handling
    let notifyName = get(message, this.selectors.msg.notifyName);
    const notifyNameAttr = !notifyName ? this.selectors.msg.senderNotifyNameArray.find((item) => get(message, item)) : undefined;
    notifyName = notifyName || !notifyNameAttr ? notifyName : get(message, notifyNameAttr);
    const sender: { id: string | undefined; name: string | undefined } = isSentByMe
      ? { id: this.me?.id, name: this.me?.name }
      : { id: contactId, name: contactName };
    const recipient: { id: string | undefined; name: string | undefined } = isSentByMe
      ? { id: contactId, name: contactName }
      : { id: this.me?.id, name: this.me?.name };
    const isSavedContact = Boolean(MessageScraper.getDisplayName(contact));
    const isSpamContact = !get(chat, this.selectors.selectedContact.notSpam);
    const timestamp = get(message, this.selectors.msg.t) ? get(message, this.selectors.msg.t) * 1000 : Date.now();
    const serializedMessageId = MessageScraper.getMessageId(message);
    const msgId = get(message, this.selectors.msg.id);
    const isDisappearing = this.isDisappearingMessage(message);
    const isForwarded = get(message, this.selectors.msg.isForwarded ?? "isForwarded") === true;

    const loadMedia = async (timeout: number) => {
      if (!isMediaMessage) {
        return;
      }

      if (!this.settings.mediaMessageTypes.has(type)) {
        const res: SerializedMessageMedia = { invalid: "messageType", fileName: "tuvis", mimeType: DEFAULT_MIME_TYPE, size: 0 };
        return res;
      }

      const messageId = MessageScraper.getMessageId(message);
      if (!messageId) {
        return;
      }

      return this.mediaLimit(() =>
        Promise.race([this.downloadMessageMedia(message, type), new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), timeout))])
      );
    };

    const loadSignedMedia = async (timeout: number) => {
      const messageId = MessageScraper.getMessageId(message);
      if (!isMediaMessage || !messageId) return;
      return this.mediaLimit(() => {
        const controller = new AbortController();
        return Promise.race([
          this.getSignedMedia(message, type, controller.signal),
          new Promise<undefined>((resolve) =>
            setTimeout(() => {
              controller.abort();
              resolve(undefined);
            }, timeout)
          ),
        ]);
      });
    };

    const msg: EmittedMessage = {
      chatId: this.getModelSerializedId(chat),
      senderId: sender.id ?? "",
      senderName: sender.name ?? "",
      senderPhone,
      receiverId: recipient.id ?? "",
      receiverName: recipient.name ?? "",
      receiverPhone,
      authorPhone,
      notifyName,
      t: get(message, this.selectors.msg.t),
      timestamp,
      text: get(message, this.selectors.msg.textOptions[raw_type as "chat" | "video"] ?? this.selectors.msg.textOptions.default),
      serializedMessageId,
      id: msgId,
      shortId: msgId.substring(msgId.length - 6, msgId.length),
      isSentByMe,
      isSavedContact,
      isSpamContact,
      isGroupMsg: MessageScraper.getIsGroup(chat, sender.id ?? "", recipient.id ?? ""),
      startOfDay: get(message, this.selectors.msg.startOfDay) ?? new Date(timestamp).setHours(0, 0, 0, 0) / 1000,
      quotedMsgId: MessageScraper.getShortQuotedMessageId(message),
      latestUpdate: await this.getLatestMessageUpdate(message),
      type,
      isDisappearing,
      isForwarded,
      chatType: "chat",
      channelType: "whatsAppWeb",
      async loadMedia(timeout = ms("2m")) {
        const scraped = msg as EmittedMessage & SerializedMessage;
        scraped.media ??= await loadMedia(timeout);
        return Object.assign({}, msg, { loadMedia: undefined, loadSignedMedia: undefined }) as SerializedMessage;
      },
      async loadSignedMedia(timeout = ms("2m")) {
        const scraped = msg as EmittedMessage & SerializedMessage;
        const result = await loadSignedMedia(timeout);
        if (result) {
          scraped.deferredMedia = result.deferredMedia;
          scraped.media = result.media;
        }
        return Object.assign({}, msg, { loadSignedMedia: undefined, loadMedia: undefined }) as SerializedMessage;
      },
    };

    if (type === "poll_creation") {
      const name = get(message, this.selectors.msg.pollName);
      const rawOptions = get(message, this.selectors.msg.pollOptions);
      const optionsArray = Array.isArray(rawOptions) ? rawOptions : rawOptions ? [rawOptions] : [];
      const options = optionsArray.map((opt: any) => get(opt, this.selectors.msg.pollOptionName) ?? String(opt)).filter(Boolean);
      const optionsText = options.map((option) => `${option}\n`);

      msg.pollData = { name, options };
      msg.text = `${name}${optionsText ? ` [${optionsText}]` : ""}`;
    }

    if (type === "event_creation") {
      const name = get(message, this.selectors.msg.eventName);
      const description = get(message, this.selectors.msg.eventDescription);
      const start = get(message, this.selectors.msg.eventStartTime);
      const end = get(message, this.selectors.msg.eventEndTime);

      const parts = [name];

      if (start) {
        const startFormatted = dayjs(start * 1000).format("M/D/YYYY, HH:mm");
        const endFormatted = end ? dayjs(end * 1000).format("M/D/YYYY, HH:mm") : undefined;
        const timeRange = startFormatted + (endFormatted ? ` - ${endFormatted}` : "");
        parts.push(timeRange);
      }

      if (description) {
        parts.push(`Description: ${description}`);
      }

      msg.text = `Event shared: ${parts.join(" | ")}`;
    }

    if (MessageScraper.getIsStatus(message)) {
      msg.chatType = "status";
    } else if (MessageScraper.getIsNewsletter(message)) {
      msg.chatType = "newsletter";
    } else if (MessageScraper.getIsBroadcast(message)) {
      msg.chatType = "broadcast";
    }

    if (isSentByMe) {
      msg.ack = this.selectors.ack.ackKeys[get(message, this.selectors.ack.fieldName)] as "read" | "delivered" | "sent" | undefined;
    }

    const vcards = this.getVcardNames(message);
    if (vcards) {
      msg.vcards = vcards;
      msg.text = `${vcards.count === 1 ? "Contact shared" : "Contacts shared"}:\n${vcards.names}`;
    }

    if (type === "location") {
      const lat = get(message, this.selectors.msg.locationLat);
      const lng = get(message, this.selectors.msg.locationLng);
      if (lat !== undefined && lng !== undefined) {
        const latitude = Number(lat);
        const longitude = Number(lng);
        if (!Number.isNaN(latitude) && !Number.isNaN(longitude)) {
          msg.location = { latitude, longitude };
          msg.text = `Latitude: ${latitude}, Longitude: ${longitude}`;
        }
      }
    }

    if (type === "call_log") {
      const call = this.extractCallData(message);
      msg.text = `${call.outcome} ${call.direction} WhatsApp call from ${call.from} to ${call.to}`;
    }

    if (type === "gp2") {
      msg.subtype = get(message, this.selectors.msg.subtype);
      const rawRecipients: any[] = get(message, "recipients") ?? [];
      const recipientIds: string[] = rawRecipients.map((r: any) => r?._serialized ?? (typeof r === "string" ? r : undefined)).filter(Boolean);
      const ids = recipientIds.length === 0 && msg.subtype === "leave" && authorPhone ? [`${authorPhone}@c.us`] : recipientIds;
      const contactModels: any[] = this.whatsappAPI?.Contact.getModelsArray() ?? [];
      const contactBySerializedId = new Map<string, any>(contactModels.map((c: any) => [get(c, "id._serialized"), c]));
      const resolvePhone = (serialized: string): string | undefined => {
        if (serialized.endsWith("@c.us")) return serialized.slice(0, -"@c.us".length);
        if (serialized.endsWith(WAP_LID_SUFFIX)) {
          try {
            const WAWebApiContact = getNamespace(this.selectors.ng?.WAWebApiContact?.find ?? ["WAWebApiContact"]);
            const getPhoneNumber = get(WAWebApiContact, this.selectors.ng?.WAWebApiContact?.getPhoneNumber ?? "getPhoneNumber");
            const contactModel = contactBySerializedId.get(serialized);
            if (!contactModel) return serialized.split("@")[0];
            const lid = getPhoneNumber?.bind(WAWebApiContact)(contactModel.id);
            const lidFn = get(lid, this.selectors.ng?.WAWebApiContact?.lidFnBind ?? "toString");
            return lidFn?.bind(lid)()?.split("@")[0];
          } catch {
            return serialized.split("@")[0];
          }
        }
        return serialized.split("@")[0];
      };
      msg.affectedParticipants = ids.flatMap((serialized) => {
        const phone = resolvePhone(serialized);
        if (!phone) return [];
        const contact = contactBySerializedId.get(serialized);
        const name: string | undefined = contact ? (MessageScraper.getDisplayName(contact as ContactModel) as string | undefined) : undefined;
        return [{ phone, name: name || undefined }];
      });
      msg.text = msg.subtype === "subject" ? (get(message, this.selectors.msg.textOptions.default) ?? msg.subtype) : msg.subtype;
    }

    // revoked = 'deleted'
    msg.isDeleted = type === "revoked";
    if (type === "revoked") {
      const originalDeletedMessageId = get(message, this.selectors.msg.originalDeletedMessageId) || msg.id;
      msg.id = originalDeletedMessageId;
      msg.shortId = originalDeletedMessageId.substring(originalDeletedMessageId.length - 6, originalDeletedMessageId.length);
      msg.text = `This message was deleted`;
      msg.latestUpdate = (get(message, this.selectors.msg.revokedTimestamp) ?? 0) * 1000;
    }

    // Check if message was edited
    const editTimestamp = get(message, this.selectors.msg.editTimestamp) || 0;
    msg.isEdited = editTimestamp > 0;

    if (isDisappearing && !msg.text) {
      msg.text = `Disappearing message`;
    }

    if (msg.isGroupMsg && this.settings.collectGroupParticipants) {
      msg.groupParticipants = get(chat, this.selectors.selectedContactGroupParticipants.models)?.map((model: any) => {
        const id = MessageScraper.getModelIdFromModel(model);
        return this.extractPhoneFromId(id) ?? get(model, this.selectors.selectedContactGroupParticipants.phone);
      });
    }

    return msg;
  }

  private coercedMessageType(message: Message) {
    const type = get(message, this.selectors.msg.type ?? "type");
    const text = () => get(message, this.selectors.msg.textOptions[type as "chat" | "video"] ?? this.selectors.msg.textOptions.default);
    if (DEFAULT_SCRAPER_SETTINGS.mediaMessageTypes.has(type) && !this.settings.mediaMessageTypes.has(type) && text()) {
      // coerce media message to text message
      return "chat";
    }
    return type;
  }

  private isValidMessage(message: Message) {
    if (this.settings.messageIdsToSync) {
      const messageId = MessageScraper.getMessageId(message);
      return this.settings.messageIdsToSync.has(messageId);
    }
    const type = this.coercedMessageType(message);
    const maxAge = (Date.now() - this.settings.maxMessageAge) / 1000;
    const isAllowedType =
      type === "gp2" ||
      this.settings.textMessageTypes.has(type) ||
      this.settings.vcardMessageTypes.has(type) ||
      this.settings.mediaMessageTypes.has(type) ||
      this.settings.pollMessageTypes?.has(type) ||
      this.settings.eventMessageTypes?.has(type);

    const messageTimestamp = get(message, this.selectors.msg.t) ?? 0;
    const { syncTimestampRange } = this.settings;
    const oldestAllowed = syncTimestampRange ? syncTimestampRange.oldest / 1000 : maxAge;
    const newestAllowed = syncTimestampRange ? syncTimestampRange.newest / 1000 : Infinity;
    const isWithinAgeLimit = messageTimestamp >= oldestAllowed && messageTimestamp <= newestAllowed;

    const isStatus = MessageScraper.getIsStatus(message);
    const isNewsletter = MessageScraper.getIsNewsletter(message);
    const isAllowedStatus = !isStatus || (isStatus && get(message, this.selectors.msg.isSentByMe) && this.settings.syncStatusMessages);
    const isAllowedNewsletter = !isNewsletter || (isNewsletter && this.settings.syncNewsLetterMessages);
    return isAllowedType && isWithinAgeLimit && isAllowedStatus && isAllowedNewsletter;
  }

  private getChatMessages(chat: ModelType) {
    const messages = (get(chat, this.selectors.getMsgs.fn).call(chat) ?? []) as Message[];
    return messages.filter((message: Message) => this.isValidMessage(message));
  }

  private getAllMsgs() {
    const chats: ModelType[] = [];
    if (this.chatModel === AllChats) {
      chats.push(...get(this.whatsappAPI, this.selectors.chat.models));
      if (this.settings.syncStatusMessages) {
        chats.push(...get(this.whatsappAPI, this.selectors.status.chat.models));
      }
    } else if (Array.isArray(this.chatModel)) {
      chats.push(...this.chatModel);
    } else {
      chats.push(this.chatModel);
    }
    return chats.flatMap((chat) => this.getChatMessages(chat).map((message) => ({ message, chat })));
  }

  private extractCallData(msg: Message): CallData {
    const from = get(msg, this.selectors.msg.senderPhone);
    const to = get(msg, this.selectors.msg.recipientPhone);
    const callType = get(msg, this.selectors.call.callType) ? "Video" : "Audio";
    const rawOutcome = get(msg, this.selectors.call.callOutcome);
    const outcome = rawOutcome === "AcceptedElsewhere" ? "Completed" : rawOutcome || "Unknown";
    const isOutgoing = from === this.me?.phone;
    const direction = isOutgoing ? "Outgoing" : "Incoming";

    const text = `${outcome} ${direction} WhatsApp ${callType} call from ${from} to ${to}`;

    return {
      timestamp: get(msg, this.selectors.msg.t),
      date: new Date(get(msg, this.selectors.msg.t) * 1000).toLocaleString(),
      from,
      to,
      outcome,
      callType,
      text,
      direction,
      id: MessageScraper.getMessageId(msg),
    };
  }

  private get newMessagesIterator() {
    if (!this.settings.listenToNewMessages) {
      return [];
    }
    const newsLetterEvents: ("newsLetterMessage" | "newsLetterEditMessage" | "newsLetterMessageAck")[] = [];
    if (this.settings.syncNewsLetterMessages) {
      newsLetterEvents.push("newsLetterMessage", "newsLetterEditMessage");
      if (this.settings.emitAckChanges) {
        newsLetterEvents.push("newsLetterMessageAck");
      }
    }
    const events = this.settings.emitAckChanges
      ? ["newMessage", "editMessage", "messageAck", ...newsLetterEvents]
      : ["newMessage", "editMessage", ...newsLetterEvents];
    return pEventIterator(this, events, { rejectionEvents: [], signal: this.settings.abortSignal });
  }

  async *[Symbol.asyncIterator](): AsyncGenerator<EmittedMessage> {
    await this.scrollToEarliest();
    try {
      if (!this.settings.ignoreOldMessages) {
        for (const { message, chat } of this.getAllMsgs()) {
          this.settings.abortSignal.throwIfAborted();
          const serializedMsg = await this.serializeMessage(message, chat);
          if (!isPhoneAllowed(this.settings, serializedMsg)) {
            continue;
          }
          yield serializedMsg;
        }
      }
      for await (const { message, chat } of this.newMessagesIterator) {
        this.settings.abortSignal.throwIfAborted();
        if (!this.isValidMessage(message)) {
          continue;
        }
        const serializedMsg = await this.serializeMessage(message, chat);
        if (!isPhoneAllowed(this.settings, serializedMsg)) {
          continue;
        }
        yield serializedMsg;
      }
    } catch (e) {
      if (this.settings.abortSignal.aborted) {
        return;
      }
      throw e;
    }
  }
}
